/**
 * Artifact bulk-search descriptor — adapts SearchArtifacts to BulkSearchResults.
 * The queried ids go in as artifactIds; status/state/type are server-side
 * PropertyFilter refinements (type via additionalFilters.artifactType).
 */
import Badge from '@cloudscape-design/components/badge';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { formatLatency } from 'src/utils/formatters';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiArtifactSummary,
  ArtifactFilters,
  SearchArtifactsInput,
} from 'src/store/slices/searchArtifacts/searchArtifactsApi';
import {
  searchArtifacts, searchArtifactsRegion, clearSearchArtifacts,
} from 'src/store/slices/searchArtifacts/searchArtifactsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
  joinList,
  refTypeColumns,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
  { key: 'type', propertyLabel: 'Type', groupValuesLabel: 'Type values', operators: ['='] },
];

/** Columns shown by default; every other column stays toggleable in Preferences. */
const DEFAULT_VISIBLE = ['id', 'artifactType', 'beaconStatus', 'statusCode', 'system', 'direction', 'payments', 'executions', 'totalLatency', 'updatedAt'];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchArtifactsInput => {
  const f: ArtifactFilters = { artifactIds: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconArtifactStatus = status;
  if (state) f.beaconArtifactState = state;
  if (filters.type?.length) f.additionalFilters = { artifactType: filters.type };
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const artifactSearchDescriptor: BulkSearchDescriptor<ApiArtifactSummary> = {
  entity: 'artifact',
  title: 'Bulk artifact search',
  path: '/artifacts',
  bulkPlaceholder: 'Enter multiple artifact IDs, one per line',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'Artifact id',
      cell: (a) => <DetailLink to={`/artifact?id=${encodeURIComponent(a.id)}`} newTab>{a.id}</DetailLink>,
    },
    { id: 'artifactType', header: 'Type', cell: (a) => (a.artifactType ? <Badge>{a.artifactType}</Badge> : '-') },
    { id: 'beaconStatus', header: 'Beacon status', cell: (a) => <StatusCell state={a.beaconArtifactState} label={a.beaconArtifactStatus} /> },
    { id: 'beaconState', header: 'Beacon state', cell: (a) => dash(a.beaconArtifactState) },
    { id: 'statusCode', header: 'Status code', cell: (a) => dash(a.statusCode) },
    { id: 'system', header: 'System', cell: (a) => dash(a.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (a) => (a.currentDirection ? titleCase(a.currentDirection) : '-') },
    { id: 'systemsInvolved', header: 'Systems involved', cell: (a) => joinList(a.systemsInvolved) },
    { id: 'payments', header: 'Payments', cell: (a) => a.paymentIds?.length ?? 0 },
    { id: 'executions', header: 'Executions', cell: (a) => a.executionCount ?? 0 },
    { id: 'currentStatusAge', header: 'Status age', cell: (a) => formatLatency(a.currentStatusAgeMs) },
    { id: 'totalLatency', header: 'Total latency', cell: (a) => formatLatency(a.totalLatencyMs) },
    { id: 'correlationTags', header: 'Correlation tags', cell: (a) => joinList(a.correlationTags) },
    { id: 'createdAt', header: 'Created', cell: (a) => (a.createdAt ? fullTimestamp(a.createdAt) : '-') },
    { id: 'updatedAt', header: 'Updated', cell: (a) => (a.updatedAt ? fullTimestamp(a.updatedAt) : '-') },
  ],
  defaultVisibleColumns: DEFAULT_VISIBLE,
  // One column per artifact type — header = type, cell = this artifact's id.
  dynamicColumns: (items) => refTypeColumns(items, (a) => [{ type: a.artifactType, id: a.id }]),
  run: (dispatch, args) => { dispatch(searchArtifacts(buildInput(args))); },
  runRegion: (dispatch, { region, ...args }) => {
    dispatch(searchArtifactsRegion({ input: buildInput(args), region }));
  },
  clear: (dispatch) => { dispatch(clearSearchArtifacts()); },
  select: (s) => ({
    items: s.searchArtifacts.artifacts,
    total: s.searchArtifacts.total,
    loading: s.searchArtifacts.loading,
    error: s.searchArtifacts.error,
    regionResults: s.searchArtifacts.regionResults,
    searchedRegions: s.searchArtifacts.searchedRegions,
    regionErrors: s.searchArtifacts.regionErrors,
    loadingRegions: s.searchArtifacts.loadingRegions,
  }),
};
